import React from 'react';
import ReactDOM from 'react-dom';
import { ResponsiveImage, ResponsiveImageSize } from 'react-responsive-image';
import styleHelper from './../helpers/style';

class Image extends React.Component {
  constructor(props) {
    super(props);


    // used by React as the component name
    this.displayName = 'Image';


    // Amount of pixels before the element is scrolled into view that
    // the image should be loaded
    this.lazyLoadOffset = 500;


    // Amount of pixels before the element is scrolled into view that
    // the transitionedIn state property should be set
    this.transitionInOffset = -100;


    // initial state object
    this.state = {
      show: !this.props.lazy,
      transitionedIn: false,
      imageStyle: null,
    };


    // bind functions to component
    this.renderImage = this.renderImage.bind(this);
    this.renderCredit = this.renderCredit.bind(this);
    this.setFullBleedImageStyle = this.setFullBleedImageStyle.bind(this);
    this.loadImageIfInViewport = this.loadImageIfInViewport.bind(this);
    this.setTransitionedInIfInViewport =
      this.setTransitionedInIfInViewport.bind(this);
    this.getClassName = this.getClassName.bind(this);
  }


  /**
   * Called after component mounts to DOM
   * @returns {undefined} undefined
   */
  componentDidMount() {
    this.node = ReactDOM.findDOMNode(this);

    if (this.props.fullBleed) {
      this.setFullBleedImageStyle();
    }

    this.loadImageIfInViewport();
  }


  /**
   * Called when component updates
   * @param {Object} prevProps - The previous props object
   * @returns {undefined} undefined
   */
  componentDidUpdate(prevProps) {
    this.loadImageIfInViewport();
    this.setTransitionedInIfInViewport();

    if (this.props.fullBleed &&
      this.props.windowSize &&
      ((this.props.windowSize.width !== prevProps.windowSize.width) ||
      (this.props.windowSize.height !== prevProps.windowSize.height))) {
      this.setFullBleedImageStyle();
    }
  }


  /**
   * Adds a class the image if in the viewport
   * @returns {undefined} undefined
   */
  setTransitionedInIfInViewport() {
    if (this.props.lazy && typeof window !== 'undefined') {
      const bounds = this.node.getBoundingClientRect();

      if (bounds.top <= (window.innerHeight + this.transitionInOffset) &&
        bounds.bottom >= (0 - this.transitionInOffset) &&
        !this.state.transitionedIn) {
        this.setState({ transitionedIn: true });
      }
    }
  }


  /**
   * Loads the image if in the viewport
   * @returns {undefined} undefined
   */
  loadImageIfInViewport() {
    if (this.props.lazy && typeof window !== 'undefined') {
      const bounds = this.node.getBoundingClientRect();

      if (bounds.top <= (window.innerHeight + this.lazyLoadOffset) &&
        bounds.bottom >= (0 - this.lazyLoadOffset) &&
        !this.state.show) {
        this.setState({ show: true });
      }
    }
  }


  /**
   * Renders image
   * @returns {ReactElement} React element
   */
  renderImage() {
    const data = this.props.data;
    let element;

    if (this.state.show) {
      element = (
        <ResponsiveImage
          alt={data.alt || 'Bleacher Report'}
          imageStyle={this.state.imageStyle}
        >
          <ResponsiveImageSize
            default
            minWidth={0}
            path={data.portrait.small.url}
          />
          <ResponsiveImageSize
            minWidth={300}
            path={data.portrait.large.url}
          />
          <ResponsiveImageSize
            minWidth={768}
            path={data.landscape.large.url}
          />
          <ResponsiveImageSize
            minWidth={1100}
            path={data.landscape.largest.url}
          />
        </ResponsiveImage>
      );
    }

    return element;
  }


  /**
   * Renders credit
   * @returns {ReactElement} React element
   */
  renderCredit() {
    const data = this.props.data;
    let element;

    if (data.credit) {
      element = (
        <span className='credit'>{data.credit}</span>
      );
    }

    return element;
  }


  /**
   * Sets full bleed style for the image
   * @returns {Object} A style object
   */
  setFullBleedImageStyle() {
    const data = this.props.data;
    let currentSizeRef = data.portrait.small;

    if (this.props.windowSize > 767) {
      currentSizeRef = data.landscape.small;
    }

    const imageStyle = styleHelper.getSizeToCover(
      Number(currentSizeRef.width),
      Number(currentSizeRef.height),
      this.node.offsetWidth,
      this.node.offsetHeight
    );

    this.setState({ imageStyle });
  }


  /**
   * Gets the component className
   * @returns {String} A className string
   */
  getClassName() {
    const data = this.props.data;
    const dataId = data.id ? data.id : '';
    const transitionedInClass =
      this.state.transitionedIn ? ' transitioned-in' : '';
    let gridPushClass = this.props.gridPush ?
      ' push-' + this.props.gridPush : '';
    if (gridPushClass !== '' && this.props.gridPushGutter) {
      gridPushClass = ' push-' + this.props.gridPush + '-gutter';
    }
    let gridWidthClass = this.props.gridWidth ?
      ' col-' + this.props.gridWidth : '';
    if (gridWidthClass.length) {
      if (this.props.gridWidth === 'full') {
        gridWidthClass = ' full-width';
      }

      if (this.props.gridWidth === 'half left') {
        gridWidthClass = ' half-width left';
      }

      if (this.props.gridWidth === 'half right') {
        gridWidthClass = ' half-width right';
      }
    }
    let largeGridPushClass = this.props.largeGridPush ?
      ' large-push-' + this.props.largeGridPush : '';
    if (largeGridPushClass !== '' && this.props.largeGridPushGutter) {
      largeGridPushClass = ' large-push-' + this.props.largeGridPush;
    }
    let largeGridWidthClass = this.props.largeGridWidth ?
      ' large-col-' + this.props.largeGridWidth : '';
    if (largeGridWidthClass.length) {
      if (this.props.largeGridWidth === 'full') {
        largeGridWidthClass = ' full-width';
      }

      if (this.props.largeGridWidth === 'half left') {
        largeGridWidthClass = ' half-width left';
      }

      if (this.props.largeGridWidth === 'half right') {
        largeGridWidthClass = ' half-width right';
      }
    }

    return 'component-image section-' + this.props.iteration + '-' +
      dataId + ' prev-section-type-' + this.props.prevSectionType +
      gridPushClass +
      gridWidthClass +
      largeGridPushClass +
      largeGridWidthClass +
      transitionedInClass;
  }


  /**
   * Renders component
   * @returns {ReactElement} React element
   */
  render() {
    const image = this.renderImage();
    const credit = this.renderCredit();
    const className = this.getClassName();

    return (
      <section className={className}>
        {image}
        {credit}
      </section>
    );
  }
}


/**
 * Expected propTypes
 * @prop {Object} data - The image data
 * @prop {Boolean} fullBleed - If the image should be full
 * bleed in its container
 * @prop {Number} gridPush - The amount of grid columns to push the content from
 * the left
 * @prop {Boolean} gridPushGutter -  If extra push should be applied to account
 * for the grid gutter width
 * @prop {Number} gridWidth - The width of the element, in grid columns
 * @prop {Number} iteration - The iteration of the element
 * @prop {Number} largeGridPush - The amount of grid columns to push the content from
 * the left
 * @prop {Boolean} largeGridPushGutter -  If extra push should be applied to account
 * for the grid gutter width
 * @prop {Number} largeGridWidth - The width of the element, in grid columns
 * @prop {Boolean} lazy - If the component should only display when scrolled
 * into view
 * @prop {String} prevSectionType - The type of the previous section
 * @prop {Number} scrollTop - The amount, in pixels, the user has scrolled in
 * the entire article
 * @prop {Object} windowSize - An object holding the width
 * and size of the window
 */
Image.propTypes = {
  data: React.PropTypes.object,
  fullBleed: React.PropTypes.bool,
  gridPush: React.PropTypes.oneOfType([
    React.PropTypes.string,
    React.PropTypes.number,
  ]),
  gridPushGutter: React.PropTypes.bool,
  gridWidth: React.PropTypes.oneOfType([
    React.PropTypes.string,
    React.PropTypes.number,
  ]),
  iteration: React.PropTypes.number,
  largeGridPush: React.PropTypes.oneOfType([
    React.PropTypes.string,
    React.PropTypes.number,
  ]),
  largeGridPushGutter: React.PropTypes.bool,
  largeGridWidth: React.PropTypes.oneOfType([
    React.PropTypes.string,
    React.PropTypes.number,
  ]),
  lazy: React.PropTypes.bool,
  prevSectionType: React.PropTypes.string,
  scrollTop: React.PropTypes.number,
  windowSize: React.PropTypes.object,
};


export default Image;



/** WEBPACK FOOTER **
 ** ./assets/js/src/components/Image.jsx
 **/